// src/components/governance/CWCPortal.tsx
// DROP 26B — Central Working Committee (CWC) Full Portal
// MIDDLE-LEVEL GOVERNANCE: Operational coordination, dept management
// Management Committee — turns Board strategy into actionable plans

import React, { useState, useEffect, useCallback } from 'react';
import BudgetManager from '../finance/BudgetManager';
import ReportsModule from '../../components/admin/ReportsModule';
import {
  Crown, Users, Briefcase, Target,
  CheckCircle, Plus,
  Loader2, AlertCircle, ChevronRight,
  FileText, Layers, Activity
} from 'lucide-react';

interface Member {
  id: string; name: string; role: string; village?: string;
  title?: string; bio?: string; phone?: string; email?: string;
  occupation?: string; organization?: string; state?: string;
  photoUrl?: string; sortOrder?: number; isActive?: boolean;
}

interface Department {
  id: string; name: string; lead: string; memberCount: number;
  focus: string; status: 'active' | 'planning' | 'completed';
  tasks: string[];
}

interface WorkflowItem {
  id: string; title: string; department: string; assignee: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  priority: 'high' | 'medium' | 'low'; dueDate: string; progress: number;
}

const C = {
  bg: '#070D0A', surface: '#0E1812', card: '#121E16',
  border: '#1C2E22', gold: '#C9A84C', goldLt: '#E2C97E',
  text: '#EDE8DC', muted: 'rgba(237,232,220,0.5)',
  dim: 'rgba(237,232,220,0.25)', green: '#0F5132', darkGreen: '#0A3D26',
};

function TabBtn({ active, onClick, children, icon }:
  { key?: React.Key; active: boolean; onClick: () => void; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
      background: active ? C.green : 'transparent',
      color: active ? C.goldLt : C.muted, fontSize: 13,
      fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' as const,
      borderBottom: active ? `2px solid ${C.gold}` : '2px solid transparent',
    }}>
      {icon && <span>{icon}</span>}{children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, { bg: string; color: string; label: string }> = {
    'in-progress': { bg: 'rgba(201,168,76,0.12)', color: '#C9A84C', label: '⟳ In Progress' },
    'completed':   { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✓ Completed' },
    'pending':     { bg: 'rgba(99,102,241,0.12)', color: '#818cf8', label: '○ Pending' },
    'blocked':     { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: '⊘ Blocked' },
    'active':      { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '● Active' },
    'planning':    { bg: 'rgba(201,168,76,0.12)', color: '#C9A84C', label: '📋 Planning' },
  };
  const s = m[status] || m.pending;
  return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
    background: s.bg, color: s.color }}>{s.label}</span>;
}

function PriorityBadge({ p }: { p: string }) {
  const m: Record<string, { color: string; label: string }> = {
    high:   { color: '#ef4444', label: '🔴 High' },
    medium: { color: C.gold,   label: '🟡 Medium' },
    low:    { color: '#22c55e', label: '🟢 Low' },
  };
  const s = m[p] || m.medium;
  return <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>{s.label}</span>;
}

// DROP 34 — departments moved to state
const DEFAULT_DEPTS: Department[] = [
  { id: 'd1', name: 'Programme & Events', lead: 'TBD', memberCount: 0, status: 'active',
    focus: 'Schedule coordination, event sequencing, Dec 24–30 daily programme management',
    tasks: ['Finalize daily programme', 'Book performers', 'Coordinate venue layout', 'Schedule traditional events'] },
  { id: 'd2', name: 'Logistics & Transport', lead: 'TBD', memberCount: 0, status: 'active',
    focus: 'Movement of dignitaries, equipment transport, venue setup, security coordination',
    tasks: ['Arrange VIP transportation', 'Coordinate security deployment', 'Manage equipment logistics'] },
  { id: 'd3', name: 'Media & Publicity', lead: 'TBD', memberCount: 0, status: 'planning',
    focus: 'Press releases, social media, live stream setup, photography, videography',
    tasks: ['Launch social media campaign', 'Set up live stream infrastructure', 'Engage press corps'] },
  { id: 'd4', name: 'Hospitality & Protocol', lead: 'TBD', memberCount: 0, status: 'planning',
    focus: 'Guest accommodation, dignitaries reception, catering, palace protocol',
    tasks: ['Book accommodation for VIPs', 'Plan royal banquet menu', 'Brief protocol officers'] },
  { id: 'd5', name: 'Finance & Procurement', lead: 'TBD', memberCount: 0, status: 'active',
    focus: 'Budget management, vendor payments, procurement, fundraising coordination',
    tasks: ['Process vendor payments', 'Track fundraising receipts', 'Manage petty cash'] },
  { id: 'd6', name: 'Community Liaison', lead: 'TBD', memberCount: 0, status: 'active',
    focus: 'Coordinate with 22 village LOCs, diaspora community, registration drives',
    tasks: ['Activate all 22 LOCs', 'Community registration drives', 'Diaspora coordination'] },
];

const DEFAULT_WORKFLOWS: WorkflowItem[] = [
  { id: 'w1', title: 'Festival Programme Booklet Production', department: 'Media & Publicity',
    assignee: 'Media Lead', status: 'in-progress', priority: 'high', dueDate: '2026-11-30', progress: 40 },
  { id: 'w2', title: 'Venue Setup & Decoration Plan', department: 'Logistics & Transport',
    assignee: 'Logistics Lead', status: 'pending', priority: 'high', dueDate: '2026-12-01', progress: 10 },
  { id: 'w3', title: 'VIP Guest Invitation & RSVP', department: 'Hospitality & Protocol',
    assignee: 'Protocol Officer', status: 'in-progress', priority: 'high', dueDate: '2026-11-15', progress: 65 },
  { id: 'w4', title: 'Community Registration Drive Launch', department: 'Community Liaison',
    assignee: 'Liaison Officer', status: 'completed', priority: 'medium', dueDate: '2026-10-31', progress: 100 },
  { id: 'w5', title: 'Live Stream Infrastructure Setup', department: 'Media & Publicity',
    assignee: 'Tech Team', status: 'pending', priority: 'medium', dueDate: '2026-12-05', progress: 0 },
  { id: 'w6', title: 'Security Deployment Plan', department: 'Logistics & Transport',
    assignee: 'Security Lead', status: 'in-progress', priority: 'high', dueDate: '2026-12-10', progress: 30 },
];

interface Props { currentUser?: { email: string; systemRole?: string } | null; }

export default function CWCPortal({ currentUser }: Props) {
  const [tab, setTab] = useState<'overview' | 'members' | 'departments' | 'workflows' | 'finance' | 'minutes'>('overview');
  const [finSubTab, setFinSubTab] = useState<'budget' | 'reports'>('budget');
  const [members, setMembers] = useState<Member[]>([]);
  const [departments, setDepartments] = useState<Department[]>(DEFAULT_DEPTS);
  const [workflows, setWorkflows] = useState<WorkflowItem[]>(DEFAULT_WORKFLOWS);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [expandedDept, setExpandedDept] = useState<string | null>(null);
  const [showWfForm, setShowWfForm] = useState(false);
  // DROP 34 — Add member form state
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberForm, setMemberForm] = useState({ name:'', role:'Member', village:'', title:'', phone:'', email:'' });
  const [savingMember, setSavingMember] = useState(false);
  // DROP 34 — Add department form state
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [deptForm, setDeptForm] = useState({ name:'', lead:'TBD', focus:'' });
  const [wfForm, setWfForm] = useState({ title: '', department: departments[0]?.name || '', assignee: '', priority: 'medium', dueDate: '' });

  const flash = (type: 'ok' | 'err', text: string) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    fetch('/api/public/management-committee')
      .then(r => r.json())
      .then(d => { setMembers(d.members || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleAddWorkflow = () => {
    if (!wfForm.title.trim()) { flash('err', 'Title required'); return; }
    setWorkflows(prev => [{
      id: `w${Date.now()}`, ...wfForm as any,
      status: 'pending' as const, progress: 0,
    }, ...prev]);
    setWfForm({ title: '', department: departments[0]?.name || '', assignee: '', priority: 'medium', dueDate: '' });
    setShowWfForm(false);
    flash('ok', 'Workflow task added.');
  };

  // DROP 34 — Add CWC member
  const handleAddMember = async () => {
    if (!memberForm.name.trim()) { flash('err', 'Member name required'); return; }
    if (!memberForm.role.trim()) { flash('err', 'Role required'); return; }
    setSavingMember(true);
    const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
    try {
      const r = await fetch('/api/admin/management-committee', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ ...memberForm, isReal: true }),
      });
      const d = await r.json();
      if (d.success || d.member) {
        setMembers(prev => [...prev, d.member]);
        setMemberForm({ name:'', role:'Member', village:'', title:'', phone:'', email:'' });
        setShowMemberForm(false);
        flash('ok', `${memberForm.name} added to CWC successfully.`);
      } else { flash('err', d.error || 'Failed to add member. Ensure you are logged in as Platform Admin.'); }
    } catch { flash('err', 'Network error — check connection and try again'); }
    finally { setSavingMember(false); }
  };

  // DROP 34 — Add department
  const handleAddDept = () => {
    if (!deptForm.name.trim()) { flash('err', 'Department name required'); return; }
    const newDept: Department = {
      id: `d${Date.now()}`, name: deptForm.name.trim(),
      lead: deptForm.lead || 'TBD', memberCount: 0, status: 'planning',
      focus: deptForm.focus || `${deptForm.name} coordination and management`,
      tasks: [],
    };
    setDepartments(prev => [...prev, newDept]);
    setDeptForm({ name:'', lead:'TBD', focus:'' });
    setShowDeptForm(false);
    flash('ok', `Department "${newDept.name}" created successfully.`);
  };

  const updateWfStatus = (id: string, status: WorkflowItem['status']) => {
    setWorkflows(prev => prev.map(w => w.id === id ? { ...w, status,
      progress: status === 'completed' ? 100 : w.progress } : w));
    flash('ok', 'Status updated.');
  };

  const updateProgress = (id: string, delta: number) => {
    setWorkflows(prev => prev.map(w => w.id === id ?
      { ...w, progress: Math.max(0, Math.min(100, w.progress + delta)) } : w));
  };

  const userRole = (currentUser?.systemRole || '').toUpperCase();
  const isAdmin = ['PLATFORM_ADMIN', 'FESTIVAL_COORDINATOR'].includes(userRole) ||
                  currentUser?.email === 'erueuzairue@gmail.com';

  const chairman = members.find(m => m.role === 'Chairman');
  const active = workflows.filter(w => w.status === 'in-progress').length;
  const completed = workflows.filter(w => w.status === 'completed').length;
  const blocked = workflows.filter(w => w.status === 'blocked').length;
  const activeDepts = departments.filter(d => d.status === 'active').length;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, #0A1F14 0%, #0F5132 100%)`,
          borderRadius: 16, padding: '28px 32px', marginBottom: 24,
          border: `1px solid rgba(201,168,76,0.25)`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -30, right: 20, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(201,168,76,0.06)' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8,
                  background: 'rgba(201,168,76,0.12)', border: `1px solid rgba(201,168,76,0.25)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Layers size={18} style={{ color: C.gold }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase' as const, color: C.gold }}>
                  Middle-Level Governance · Operational Body
                </span>
              </div>
              <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: 24, fontWeight: 700,
                color: C.text, margin: 0, lineHeight: 1.2 }}>
                Central Working Committee
              </h1>
              <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0' }}>
                Operational coordination · Department management · Workflow execution · CWC governance
              </p>
            </div>
            <div style={{ fontSize: 11, color: C.dim, textAlign: 'right' as const }}>
              <div style={{ color: C.gold, fontWeight: 600, marginBottom: 4 }}>Management Committee</div>
              <div>ERUE Festival 2026</div>
              <div>December 24–30, 2026</div>
            </div>
          </div>
        </div>

        {/* Flash */}
        {msg && (
          <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'ok' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.type === 'ok' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: msg.type === 'ok' ? '#22c55e' : '#ef4444', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}

        {/* Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { icon: <Users size={16} />, label: 'CWC Members', value: members.length, color: C.gold },
            { icon: <Briefcase size={16} />, label: 'Departments', value: `${activeDepts}/${departments.length} active`, color: '#38bdf8' },
            { icon: <Activity size={16} />, label: 'Active Tasks', value: active, color: C.gold },
            { icon: <CheckCircle size={16} />, label: 'Completed', value: completed, color: '#22c55e' },
            { icon: <AlertCircle size={16} />, label: 'Blocked', value: blocked, color: '#ef4444' },
          ].map((m, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px',
              display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9,
                background: `${m.color}15`, border: `1px solid ${m.color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: m.color }}>{m.icon}</span>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{m.value}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{m.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 24, overflowX: 'auto' as const }}>
          {([
            { key: 'overview', label: 'Overview', icon: <Target size={13} /> },
            { key: 'members', label: 'Members', icon: <Users size={13} /> },
            { key: 'departments', label: 'Departments', icon: <Briefcase size={13} /> },
            { key: 'workflows', label: 'Workflows', icon: <Activity size={13} /> },
            { key: 'finance', label: 'Finance', icon: <Activity size={13} /> },
            { key: 'minutes', label: 'Minutes', icon: <FileText size={13} /> },
          ] as const).map(t => (
            <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} icon={t.icon}>
              {t.label}
            </TabBtn>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={32} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {tab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* CWC Mandate */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 14 }}>
                    ⚙️ Operational Mandate
                  </h3>
                  {[
                    { icon: '📋', title: 'Strategy to Action', desc: 'Translate Board directives into concrete operational plans across all departments' },
                    { icon: '🔗', title: 'Cross-Department Coordination', desc: 'Bridge communication between festival departments and field LOCs' },
                    { icon: '📊', title: 'Workflow Management', desc: 'Assign, track and report on all festival preparation tasks and timelines' },
                    { icon: '👥', title: 'Team Leadership', desc: 'Oversee department leads, evaluate performance and resolve operational challenges' },
                    { icon: '📈', title: 'Progress Reporting', desc: 'Provide regular status reports to the Board of Trustees on all operations' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '8px 12px',
                      background: `${C.bg}80`, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 16 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick stats */}
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 14 }}>
                  {/* Workflow summary */}
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 12 }}>
                      📊 Workflow Status
                    </h3>
                    {[
                      { label: 'In Progress', count: active, color: C.gold },
                      { label: 'Completed', count: completed, color: '#22c55e' },
                      { label: 'Pending', count: workflows.filter(w => w.status === 'pending').length, color: '#818cf8' },
                      { label: 'Blocked', count: blocked, color: '#ef4444' },
                    ].map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 0', borderBottom: i < 3 ? `1px solid ${C.border}` : 'none' }}>
                        <span style={{ fontSize: 12, color: C.muted }}>{s.label}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.count}</span>
                      </div>
                    ))}
                  </div>

                  {/* High priority items */}
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 12 }}>
                      🔴 High Priority Tasks
                    </h3>
                    {workflows.filter(w => w.priority === 'high' && w.status !== 'completed').slice(0, 4).map(w => (
                      <div key={w.id} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12, color: C.text, marginBottom: 2 }}>{w.title}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 10, color: C.dim }}>{w.department}</span>
                          <StatusBadge status={w.status} />
                        </div>
                        <div style={{ height: 3, background: C.border, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${w.progress}%`, background: C.gold, borderRadius: 2 }} />
                        </div>
                      </div>
                    ))}
                    {workflows.filter(w => w.priority === 'high' && w.status !== 'completed').length === 0 && (
                      <p style={{ fontSize: 12, color: C.dim, textAlign: 'center' as const }}>No high priority tasks pending</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── MEMBERS ── */}
            {tab === 'members' && (
              <div>
                {/* Members toolbar */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <div style={{ fontSize:11, color:C.muted }}>
                    {members.length} member{members.length !== 1 ? 's' : ''} recorded
                  </div>
                  {isAdmin && (
                  <button onClick={() => setShowMemberForm(!showMemberForm)}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px',
                      background:C.green, border:`1px solid ${C.gold}44`, borderRadius:8,
                      color:C.gold, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    + Add Member
                  </button>
                )}
                </div>
                {/* Add member form */}
                {isAdmin && showMemberForm && (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                    padding:'20px 24px', marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.gold, marginBottom:16,
                      letterSpacing:1, textTransform:'uppercase' as const }}>Add CWC Member</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                      {[
                        { label:'Full Name *', key:'name', placeholder:'Enter full name' },
                        { label:'Village', key:'village', placeholder:'e.g. Jattu-Uzairue' },
                        { label:'Title', key:'title', placeholder:'e.g. Engr., Dr., Chief' },
                        { label:'Phone', key:'phone', placeholder:'+234...' },
                        { label:'Email', key:'email', placeholder:'email@domain.com' },
                      ].map(f => (
                        <div key={f.key}>
                          <label style={{ display:'block', fontSize:10, color:C.muted,
                            textTransform:'uppercase' as const, letterSpacing:1, marginBottom:4 }}>{f.label}</label>
                          <input value={(memberForm as any)[f.key]}
                            onChange={e => setMemberForm(p => ({ ...p, [f.key]: e.target.value }))}
                            placeholder={f.placeholder}
                            style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                              borderRadius:6, padding:'8px 12px', color:C.text, fontSize:13,
                              outline:'none', boxSizing:'border-box' as const }} />
                        </div>
                      ))}
                      <div>
                        <label style={{ display:'block', fontSize:10, color:C.muted,
                          textTransform:'uppercase' as const, letterSpacing:1, marginBottom:4 }}>Role *</label>
                        <select value={memberForm.role}
                          onChange={e => setMemberForm(p => ({ ...p, role: e.target.value }))}
                          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                            borderRadius:6, padding:'8px 12px', color:C.text, fontSize:13, outline:'none' }}>
                          {['Chairman','Vice-Chairman','Secretary-General','Assistant Secretary',
                            'Financial Secretary','Publicity Secretary','Social Secretary',
                            'Welfare Officer','Legal Adviser','Ex-officio Member','Member'
                          ].map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={handleAddMember} disabled={savingMember}
                        style={{ padding:'9px 20px', background:C.green, border:`1px solid ${C.gold}44`,
                          borderRadius:8, color:C.gold, fontSize:12, fontWeight:700,
                          cursor:savingMember?'wait':'pointer', opacity:savingMember?0.6:1 }}>
                        {savingMember ? 'Saving...' : 'Save Member'}
                      </button>
                      <button onClick={() => setShowMemberForm(false)}
                        style={{ padding:'9px 20px', background:'transparent',
                          border:`1px solid ${C.border}`, borderRadius:8,
                          color:C.muted, fontSize:12, cursor:'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, marginBottom: 16 }}>
                  CWC Members ({members.length})
                </h2>
                {members.length === 0 ? (
                  <div style={{ textAlign: 'center' as const, padding: 60, color: C.dim }}>
                    <Users size={40} style={{ margin: '0 auto 12px', opacity: 0.3, display: 'block' }} />
                    <p style={{ color:C.muted, fontSize:13 }}>No members recorded yet. Add the first CWC member below.</p>
                    {isAdmin && (
                      <button onClick={() => setShowMemberForm(true)}
                        style={{ marginTop:12, padding:'8px 20px', background:C.green, border:`1px solid ${C.gold}44`,
                          borderRadius:8, color:C.gold, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        + Add First Member
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Leadership row */}
                    {[members.find(m => m.role === 'Chairman'), members.find(m => m.role === 'Vice-Chairman')]
                      .filter(Boolean).length > 0 && (
                      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' as const }}>
                        {[members.find(m => m.role === 'Chairman'), members.find(m => m.role === 'Vice-Chairman')]
                          .filter(Boolean).map((m) => m && (
                          <div key={m.id} style={{ flex: 1, minWidth: 240, background: C.card,
                            border: `2px solid rgba(201,168,76,0.3)`, borderRadius: 12, padding: 18,
                            display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ width: 52, height: 52, borderRadius: '50%',
                              background: `${C.gold}15`, border: `2px solid rgba(201,168,76,0.3)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Crown size={22} style={{ color: C.gold }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: C.gold, letterSpacing: 1, textTransform: 'uppercase' as const }}>{m.role}</div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{m.name}</div>
                              {m.village && <div style={{ fontSize: 11, color: C.muted }}>📍 {m.village}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* All members grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                      {members.filter(m => m.role !== 'Chairman' && m.role !== 'Vice-Chairman').map(m => (
                        <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <div style={{ width: 38, height: 38, borderRadius: '50%',
                              background: `${C.green}60`, border: `1px solid ${C.border}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Users size={16} style={{ color: C.muted }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: C.text,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{m.name}</div>
                              <div style={{ fontSize: 11, color: C.gold }}>{m.role}</div>
                            </div>
                          </div>
                          {m.village && <div style={{ fontSize: 11, color: C.muted }}>🏘️ {m.village}</div>}
                          {m.phone && <div style={{ fontSize: 11, color: C.muted }}>📞 {m.phone}</div>}
                          {m.email && <div style={{ fontSize: 11, color: C.muted }}>✉️ {m.email}</div>}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── DEPARTMENTS ── */}
            {tab === 'departments' && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <div style={{ fontSize:11, color:C.muted }}>{departments.length} departments</div>
                  {isAdmin && (
                  <button onClick={() => setShowDeptForm(!showDeptForm)}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px',
                      background:C.green, border:`1px solid ${C.gold}44`, borderRadius:8,
                      color:C.gold, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    + Add Department
                  </button>
                  )}
                </div>
                {isAdmin && showDeptForm && (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                    padding:'20px 24px', marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.gold, marginBottom:16,
                      letterSpacing:1, textTransform:'uppercase' as const }}>New Department</div>
                    {[
                      { label:'Department Name *', key:'name', placeholder:'e.g. Media & Communications' },
                      { label:'Lead Officer', key:'lead', placeholder:'TBD or name' },
                      { label:'Focus / Mandate', key:'focus', placeholder:'Describe the department mandate' },
                    ].map(f => (
                      <div key={f.key} style={{ marginBottom:12 }}>
                        <label style={{ display:'block', fontSize:10, color:C.muted,
                          textTransform:'uppercase' as const, letterSpacing:1, marginBottom:4 }}>{f.label}</label>
                        <input value={(deptForm as any)[f.key]}
                          onChange={e => setDeptForm(p => ({ ...p, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                            borderRadius:6, padding:'8px 12px', color:C.text, fontSize:13,
                            outline:'none', boxSizing:'border-box' as const }} />
                      </div>
                    ))}
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={handleAddDept}
                        style={{ padding:'9px 20px', background:C.green, border:`1px solid ${C.gold}44`,
                          borderRadius:8, color:C.gold, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        Create Department
                      </button>
                      <button onClick={() => setShowDeptForm(false)}
                        style={{ padding:'9px 20px', background:'transparent',
                          border:`1px solid ${C.border}`, borderRadius:8, color:C.muted,
                          fontSize:12, cursor:'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, marginBottom: 16 }}>
                  Festival Departments ({departments.length})
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                  {departments.map(d => (
                    <div key={d.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                      <button onClick={() => setExpandedDept(expandedDept === d.id ? null : d.id)}
                        style={{ width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' as const }}>
                        <div style={{ width: 40, height: 40, borderRadius: 9,
                          background: `${C.green}40`, border: `1px solid ${C.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Briefcase size={18} style={{ color: C.gold }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{d.name}</div>
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Lead: {d.lead} · {d.tasks.length} tasks</div>
                        </div>
                        <StatusBadge status={d.status} />
                        {expandedDept === d.id ?
                          <ChevronRight size={16} style={{ color: C.muted, transform: 'rotate(90deg)', transition: 'transform 0.2s' }} /> :
                          <ChevronRight size={16} style={{ color: C.muted, transition: 'transform 0.2s' }} />}
                      </button>
                      {expandedDept === d.id && (
                        <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${C.border}` }}>
                          <p style={{ fontSize: 12, color: C.muted, marginBottom: 14, marginTop: 14, lineHeight: 1.5 }}>{d.focus}</p>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: 1,
                            textTransform: 'uppercase' as const, marginBottom: 8 }}>Task Checklist</div>
                          {d.tasks.map((task, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 0', borderBottom: i < d.tasks.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                              <CheckCircle size={13} style={{ color: C.border, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: C.text }}>{task}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── WORKFLOWS ── */}
            {tab === 'workflows' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Workflow Tasks ({workflows.length})
                  </h2>
                  {isAdmin && (
                  <button onClick={() => setShowWfForm(!showWfForm)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      background: C.green, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 8,
                      color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={14} /> New Task
                  </button>
                  )}
                </div>

                {isAdmin && showWfForm && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ color: C.gold, fontSize: 13, marginBottom: 14 }}>Add Workflow Task</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {[
                        { label: 'Task Title *', key: 'title', placeholder: 'e.g. Arrange PA System for Grand Finale' },
                        { label: 'Assignee', key: 'assignee', placeholder: 'e.g. Logistics Lead' },
                        { label: 'Due Date', key: 'dueDate', placeholder: 'YYYY-MM-DD' },
                      ].map(field => (
                        <div key={field.key} style={{ gridColumn: field.key === 'title' ? '1 / -1' : 'auto' }}>
                          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                            textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>{field.label}</label>
                          <input type="text" placeholder={field.placeholder}
                            value={(wfForm as any)[field.key]}
                            onChange={e => setWfForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                            style={{ width: '100%', padding: '9px 12px', background: C.bg,
                              border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                              fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                        </div>
                      ))}
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Department</label>
                        <select value={wfForm.department}
                          onChange={e => setWfForm(prev => ({ ...prev, department: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Priority</label>
                        <select value={wfForm.priority}
                          onChange={e => setWfForm(prev => ({ ...prev, priority: e.target.value as any }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          {['high', 'medium', 'low'].map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button onClick={handleAddWorkflow}
                        style={{ padding: '8px 18px', background: C.gold, border: 'none', borderRadius: 8,
                          color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add Task</button>
                      <button onClick={() => setShowWfForm(false)}
                        style={{ padding: '8px 14px', background: 'none', border: `1px solid ${C.border}`,
                          borderRadius: 8, color: C.muted, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                  {workflows.map(w => (
                    <div key={w.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <PriorityBadge p={w.priority} />
                            <StatusBadge status={w.status} />
                          </div>
                          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: '4px 0' }}>{w.title}</h3>
                          <div style={{ fontSize: 11, color: C.dim }}>
                            {w.department} · Assigned: {w.assignee} · Due: {w.dueDate}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          {(['pending', 'in-progress', 'completed', 'blocked'] as const).map(s => (
                            <button key={s} onClick={() => updateWfStatus(w.id, s)}
                              style={{ padding: '3px 8px', background: w.status === s ? C.green : 'none',
                                border: `1px solid ${w.status === s ? C.gold : C.border}`,
                                borderRadius: 5, color: w.status === s ? C.gold : C.muted,
                                fontSize: 9, cursor: 'pointer', textTransform: 'capitalize' as const }}>
                              {s.replace('-', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={() => updateProgress(w.id, -10)}
                          style={{ width: 22, height: 22, borderRadius: 4, background: 'none',
                            border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 14, lineHeight: 1 }}>-</span>
                        </button>
                        <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${w.progress}%`,
                            background: w.progress === 100 ? '#22c55e' : w.status === 'blocked' ? '#ef4444' : C.gold,
                            borderRadius: 3, transition: 'width 0.3s' }} />
                        </div>
                        <button onClick={() => updateProgress(w.id, 10)}
                          style={{ width: 22, height: 22, borderRadius: 4, background: 'none',
                            border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
                        </button>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, minWidth: 36, textAlign: 'right' as const }}>
                          {w.progress}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── REPORTS ── */}
            {tab === 'minutes' && (
              <div>
                <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, marginBottom: 20 }}>
                  Operational Reports & Meeting Notes
                </h2>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24,
                  textAlign: 'center' as const }}>
                  <FileText size={40} style={{ color: C.muted, margin: '0 auto 12px', opacity: 0.4, display: 'block' }} />
                  <h3 style={{ fontSize: 16, color: C.text, marginBottom: 8 }}>CWC Reports Module</h3>
                  <p style={{ fontSize: 13, color: C.muted, maxWidth: 400, margin: '0 auto' }}>
                    Meeting minutes, departmental reports and CWC operational records will be managed here.
                    Contact Platform Administrator to enable report uploads.
                  </p>
                  <div style={{ marginTop: 20, padding: '14px 20px', background: `${C.bg}80`,
                    borderRadius: 10, border: `1px solid ${C.border}`, display: 'inline-block' }}>
                    <div style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>
                      📊 Overall Progress: {Math.round(workflows.reduce((s, w) => s + w.progress, 0) / Math.max(1, workflows.length))}%
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                      Across {workflows.length} tracked workflow tasks
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
